/**
 * O que faz: Seção de resultados (anúncios, follow-up, disparos) com queda segura aos cards antigos.
 * Onde usa: Dashboard, seção "Resultados por canal" abaixo dos gráficos (antes ficava no topo).
 * Conexões: useMetricas por domínio, ResultadoCanalDashboard, AcoesPrioritarias e QualidadeDados.
 * Tabelas: Nenhuma direta; consome RPCs get_metricas_*_v1 via hook compartilhado.
 * Data: 05/09/2026.
 */

import { useMemo, useState } from 'react';
import { useMetricas } from '@/features/metricas/useMetricas';
import type { ContextoMetricas } from '@/features/metricas/contextoMetricas';
import type { IndicadorMetrica } from '../../../supabase/functions/_shared/metricas/contratos';
import type { ContextoDashboard } from '@/features/dashboard/data/dashboardContracts';
import { ResultadoCanalDashboard } from './ResultadoCanalDashboard';
import { AcoesPrioritariasDashboard } from './AcoesPrioritariasDashboard';
import { QualidadeDadosDashboard } from './QualidadeDadosDashboard';
import { MetricasDetalheDialog } from './MetricasDetalheDialog';
import type { FiltroAcaoPrioritaria } from './AcoesPrioritariasDashboard';

interface SecaoResultadosMetricasProps {
  contexto: ContextoDashboard | undefined;
  periodoLabel: string;
  onDetalharAnuncios: () => void;
  onAbrirFilaPrioritaria?: (filtro: FiltroAcaoPrioritaria) => void;
}

// Fórmula em uma frase por indicador, para o modal de detalhe.
const FORMULAS: Record<string, string> = {
  leads_anuncios: 'Leads distintos criados no período com origem de anúncio identificada ou legado.',
  leads_agendaram: 'Leads distintos da coorte com ao menos um agendamento não cancelado.',
  taxa_lead_agendamento: 'Leads que agendaram divididos pelos leads da coorte, em percentual.',
  vendas_registradas: 'Quantidade de vendas registradas no período da venda.',
  leads_enviados: 'Leads únicos efetivamente enviados na coorte de follow-up.',
  leads_respondidos: 'Leads únicos com primeira resposta elegível em até 7 dias do envio.',
  taxa_resposta_leads: 'Leads que responderam divididos pelos leads enviados, em percentual.',
  mensagens_enviadas: 'Total de envios efetivos de follow-up na coorte.',
  destinatarios_enviados: 'Destinatários vinculados com envio comprovado no período.',
  mensagens_entregues: 'Mensagens com entrega comprovada entre as aceitas.',
  taxa_entrega: 'Mensagens entregues divididas pelas aceitas, em percentual.',
  leituras_observadas: 'Mensagens com leitura confirmada entre as entregues.',
};

function porId(indicadores: IndicadorMetrica[] | undefined, id: string): IndicadorMetrica | undefined {
  return indicadores?.find((item) => item.id === id);
}

function numeroDe(indicador: IndicadorMetrica | undefined): number {
  return typeof indicador?.valor === 'number' ? indicador.valor : 0;
}

export function SecaoResultadosMetricas({ contexto, periodoLabel, onDetalharAnuncios, onAbrirFilaPrioritaria }: SecaoResultadosMetricasProps) {
  // Sem período fechado (Máximo) ou sem sessão, a seção nova não consulta; os cards antigos seguem valendo.
  const contextoMetricas = useMemo<ContextoMetricas | undefined>(() => {
    if (!contexto?.usuarioId || !contexto?.clinicaId || !contexto?.periodo.inicio || !contexto?.periodo.fim) return undefined;
    return {
      usuarioId: contexto.usuarioId,
      clinicaId: contexto.clinicaId,
      modo: contexto.modo,
      versaoPermissoes: 'v1',
      periodo: { inicio: contexto.periodo.inicio, fim: contexto.periodo.fim },
      comparar: false,
      filtros: {},
    };
  }, [contexto]);

  const comercial = useMetricas('comercial', contextoMetricas);
  const followup = useMetricas('followup', contextoMetricas);
  const disparos = useMetricas('disparos', contextoMetricas);
  const atendimento = useMetricas('atendimento', contextoMetricas);
  const [detalhe, setDetalhe] = useState<{ titulo: string; indicador: IndicadorMetrica; total: number } | null>(null);

  // Backend ainda não publicado (RPC inexistente) vira ausência da seção, nunca erro na tela.
  if (!contextoMetricas || comercial.isError) return null;
  if (comercial.isLoading || !comercial.data) return null;

  const ind = (id: string) => porId(comercial.data?.indicadores, id);
  const indFu = (id: string) => porId(followup.data?.indicadores, id);
  const indDi = (id: string) => porId(disparos.data?.indicadores, id);
  const principalAnuncios = ind('leads_anuncios');
  if (!principalAnuncios) return null;

  const atualizadoEm = comercial.data?.observed_until
    ? new Date(comercial.data.observed_until).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : null;

  const abrirDetalhe = (titulo: string, indicador: IndicadorMetrica | undefined) => {
    if (!indicador) return;
    setDetalhe({ titulo, indicador, total: indicador.numerador ?? indicador.valor ?? 0 });
  };

  const semResposta = numeroDe(porId(atendimento.data?.indicadores, 'ciclos_sem_resposta_agora'));
  const aceitas = numeroDe(indDi('mensagens_aceitas'));
  const entregues = numeroDe(indDi('mensagens_entregues'));
  const falhas = Math.max(0, aceitas - entregues);

  return (
    // Seção abaixo dos gráficos: resultados por canal ficam acessíveis sem empurrar os KPIs e gráficos para baixo.
    <section aria-labelledby="titulo-resultados-canal" className="flex flex-col gap-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 id="titulo-resultados-canal" className="text-base font-semibold text-foreground">Resultados por canal</h2>
        <AcoesPrioritariasDashboard
          semResposta={semResposta}
          falhasEnvio={falhas}
          retornosVencidos={0}
          onAbrirFila={(filtro) => onAbrirFilaPrioritaria?.(filtro)}
        />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:gap-4 lg:grid-cols-3">
        <ResultadoCanalDashboard
          titulo="Anúncios"
          legendaPrincipal="leads de anúncios"
          principal={principalAnuncios}
          apoio={[ind('leads_agendaram'), ind('taxa_lead_agendamento'), ind('vendas_registradas')].filter((x): x is IndicadorMetrica => !!x)}
          periodoLabel={periodoLabel}
          atualizadoEm={atualizadoEm}
          carregando={comercial.isFetching}
          erro={null}
          onDetalhar={onDetalharAnuncios}
        />
        {!followup.isError && followup.data && indFu('leads_respondidos') && (
          <ResultadoCanalDashboard
            titulo="Follow-up"
            legendaPrincipal="leads responderam"
            principal={indFu('leads_respondidos')!}
            apoio={[indFu('leads_enviados'), indFu('taxa_resposta_leads'), indFu('mensagens_enviadas')].filter((x): x is IndicadorMetrica => !!x)}
            periodoLabel={periodoLabel}
            atualizadoEm={atualizadoEm}
            carregando={followup.isFetching}
            erro={null}
            onDetalhar={() => abrirDetalhe('Respostas do follow-up', indFu('taxa_resposta_leads'))}
          />
        )}
        {!disparos.isError && disparos.data && indDi('mensagens_entregues') && (
          <ResultadoCanalDashboard
            titulo="Disparos"
            legendaPrincipal="mensagens entregues"
            principal={indDi('mensagens_entregues')!}
            apoio={[indDi('destinatarios_enviados'), indDi('taxa_entrega'), indDi('leituras_observadas')].filter((x): x is IndicadorMetrica => !!x)}
            periodoLabel={periodoLabel}
            atualizadoEm={atualizadoEm}
            carregando={disparos.isFetching}
            erro={null}
            onDetalhar={() => abrirDetalhe('Entregas dos disparos', indDi('taxa_entrega'))}
          />
        )}
      </div>
      <QualidadeDadosDashboard
        origemDados="CRM e mídia conectada"
        qualidadeAtribuicao={`Cobertura de vínculos ${comercial.data?.cobertura.percentual ?? '—'}%`}
        coberturaVinculos={`${comercial.data?.cobertura.vinculados ?? 0} vinculados de ${comercial.data?.cobertura.elegiveis ?? 0}`}
        atualizadoEm={atualizadoEm}
      />
      {detalhe && (
        <MetricasDetalheDialog
          aberto
          aoFechar={() => setDetalhe(null)}
          titulo={detalhe.titulo}
          formula={FORMULAS[detalhe.indicador.id] ?? 'Indicador do contrato versionado de métricas.'}
          indicador={detalhe.indicador}
          itens={[]}
          total={detalhe.total}
        />
      )}
    </section>
  );
}
